import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { generateImage, getOptimalSize, ImageType } from '@/services/openai.service';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/core/redux/store';
import { setTemplateImages } from '@/core/redux/commonSlice';
// @ts-ignore - GIF import
import aiIcon from '@/assets/images/ai-icon.gif';

interface AIImageGeneratorProps {
  initialPrompt?: string;
  prompt?: string; // Controlled prompt prop - can be passed from parent
  generateAutoPrompt?: () => string;
  onSave?: (imageUrl: string) => void;
  imageType?: ImageType;
  defaultWidth?: string;
  defaultHeight?: string;
  title?: string;
  autoGenerate?: boolean;
  template?: string;
  onImageGenerated?: (imageUrl: string) => void;
  onPromptChange?: (prompt: string) => void; // Callback when prompt changes
}

const AIImageGenerator: React.FC<AIImageGeneratorProps> = ({
  initialPrompt = '',
  prompt: controlledPrompt,
  generateAutoPrompt,
  onSave,
  imageType = 'banner',
  defaultWidth = '1024',
  defaultHeight = '1024',
  title = 'AI Image Generator (DALL-E 3)',
  autoGenerate = false,
  template,
  onImageGenerated,
  onPromptChange,
}) => {
  const [showGenerator, setShowGenerator] = useState<boolean>(false);
  const [aiPrompt, setAiPrompt] = useState<string>(initialPrompt);
  const [aiImageType, setAiImageType] = useState<ImageType>(imageType);
  const [aiWidth, setAiWidth] = useState<string>(defaultWidth);
  const [aiHeight, setAiHeight] = useState<string>(defaultHeight);
  const [isGeneratingAI, setIsGeneratingAI] = useState<boolean>(false);
  const [aiGeneratedImage, setAiGeneratedImage] = useState<string>('');
  const [isSavingAI, setIsSavingAI] = useState<boolean>(false);
  const [hasGenerated, setHasGenerated] = useState<boolean>(false);

  const dispatch = useDispatch();
  const templateImages = useSelector((state: RootState) => state.common.templateImages);
  const headerData = useSelector((state: RootState) => state.common.header);
  const currentTemplate = template || headerData?.template;

  // Update prompt when initialPrompt or controlledPrompt prop changes
  useEffect(() => {
    if (controlledPrompt !== undefined) {
      setAiPrompt(controlledPrompt);
    } else if (initialPrompt) {
      setAiPrompt(initialPrompt);
    }
  }, [initialPrompt, controlledPrompt]);

  // Auto-generate when generator panel opens
  useEffect(() => {
    if (showGenerator && autoGenerate && !hasGenerated && !isGeneratingAI) {
      setIsGeneratingAI(true);
      setTimeout(() => {
        handleGenerateAIImage();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGenerator, autoGenerate]);

  // AI Image Generator Handler
  const handleGenerateAIImage = async () => {
    setIsGeneratingAI(true);
    setAiGeneratedImage('');

    try {
      const width = parseInt(aiWidth);
      const height = parseInt(aiHeight);
      const size = getOptimalSize(width, height, aiImageType);

      // Auto-generate prompt if not provided
      let promptToUse = aiPrompt.trim() || (generateAutoPrompt ? generateAutoPrompt() : '');

      if (!promptToUse) {
        toast.error('Please enter a description for the image');
        setIsGeneratingAI(false);
        return;
      }

      // Concat promptToUse with the following
      if (aiImageType === 'banner') {
        promptToUse += ', aspect ratio should be 16:9';
      } else if (aiImageType === 'image') {
        promptToUse += ', aspect ratio should be 1:1';
      } else if (aiImageType === 'icon') {
        promptToUse += ', aspect ratio should be 1:1';
      }

      // Call backend AI service
      const result = await generateImage({
        prompt: promptToUse,
        width: width,
        height: height,
        size: size,
        quality: 'standard',
        type: aiImageType,
        style: 'natural',
        template: currentTemplate || '',
      });

      setAiGeneratedImage(result.url);
      setHasGenerated(true);
      toast.success('AI Image generated successfully!');

      // Call onImageGenerated callback if provided
      if (onImageGenerated) {
        onImageGenerated(result.url);
      }

      // Log revised prompt from AI
      if (result.revised_prompt) {
        console.log('AI Revised Prompt:', result.revised_prompt);
      }

    } catch (error: any) {
      console.error('Error generating AI image:', error);
      toast.error(error.message || 'Failed to generate AI image. Please try again.');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Save AI Generated Image to Server
  const handleSaveAIImage = async () => {
    if (!aiGeneratedImage) {
      toast.error('No AI generated image to save');
      return;
    }

    setIsSavingAI(true);

    try {
      // The image is already saved on the server; just apply it to the slider
      const imageUrl = aiGeneratedImage;

      const templateKey = currentTemplate ? `template${currentTemplate}` : 'template1';
      const currentTemplateData = templateImages[templateKey] || {};
      const currentBannerImages = currentTemplateData.banner || [];
      const updatedTemplateImages = {
        ...templateImages,
        [templateKey]: {
          ...currentTemplateData,
          banner: [...currentBannerImages, { url: imageUrl }],
        },
      };
      dispatch(setTemplateImages(updatedTemplateImages));

      if (onSave) {
        onSave(imageUrl);
      }

      toast.success('Applied to slider successfully!');
      setShowGenerator(false);
    } catch (error: any) {
      console.error('Error saving AI image:', error);
      toast.error(error.message || 'Failed to save AI image. Please try again.');
    } finally {
      setIsSavingAI(false);
    }
  };

  return (
    <>
      <div className="">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            const willOpen = !showGenerator;
            setShowGenerator(willOpen);
            // Reset state when opening to allow fresh generation
            if (willOpen) {
              setHasGenerated(false);
              setAiGeneratedImage('');
            }
          }}
          className="ai-writer-btn text-decoration-none w-100 d-flex align-items-center justify-content-center gap-2 p-2"
          style={{ fontSize: '0.875rem' }}
          title="Generate AI Image"
        >
          <img 
            src={aiIcon} 
            alt="AI Icon" 
            style={{ width: '25px', height: '25px', objectFit: 'contain' }}
            onError={(e) => {
              // Fallback if image doesn't exist
              e.currentTarget.style.display = 'none';
            }}
          />
          <span className="rainbow_text_animated">Generate {title}</span>
        </a>
        <div className="text-center mt-2">
          <span className="text-muted" style={{ fontSize: '0.875rem' }}>or</span>
        </div>
      </div>

      {/* AI Image Generator Section */}
      {showGenerator && (
        <div className="card mb-3 border-primary">
          <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="fas fa-robot me-2"></i>
              {title}
            </h6>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => {
                setShowGenerator(false);
              }}
              aria-label="Close"
            ></button>
          </div>
          <div className="card-body">
            {/* Prompt Input */}
            <div className="mb-3">
              <label className="form-label">Image Description (Optional)</label>
              <input
                type="text"
                className="form-control"
                value={aiPrompt}
                onChange={(e) => {
                  const newPrompt = e.target.value;
                  setAiPrompt(newPrompt);
                  if (onPromptChange) {
                    onPromptChange(newPrompt);
                  }
                }}
                placeholder="Describe what you want to see... (e.g., Modern shopping banner with colorful products)"
              />
              <div className="alert alert-info mt-2 mb-0">
                <small>
                  <i className="fas fa-info-circle me-1"></i>
                  <strong>Tip:</strong> Provide a detailed description for a personalized image that matches your content.
                </small>
              </div>
            </div>

            {/* Auto-generating State */}
            {(isGeneratingAI || (showGenerator && !hasGenerated && !aiGeneratedImage && autoGenerate)) && (
              <div className="text-center py-4">
                <div className="mb-3">
                  <i className="fas fa-spinner fa-spin text-primary" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h6 className="text-primary mb-2">
                  <strong>Auto-generating image...</strong>
                </h6>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                  The AI is creating a professional image based on your content.
                </p>
              </div>
            )}

            {/* Generate/Regenerate Button */}
            {(!autoGenerate || hasGenerated) && (
              <button
                type="button"
                className="btn btn-primary w-100 mb-3"
                onClick={handleGenerateAIImage}
                disabled={isGeneratingAI || !aiPrompt.trim()}
              >
                {isGeneratingAI ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Generating AI Image...
                  </>
                ) : hasGenerated ? (
                  <>
                    <i className="fas fa-sync me-2"></i>
                    Re-generate Image
                  </>
                ) : (
                  <>
                    <i className="fas fa-wand-magic-sparkles me-2"></i>
                    Generate Image with AI
                  </>
                )}
              </button>
            )}

            {/* AI Generated Image Preview */}
            {aiGeneratedImage && (
              <div className="ai-preview-container">
                <label className="form-label">Generated Image Preview:</label>
                <div className="position-relative">
                  <img
                    src={aiGeneratedImage}
                    alt="AI Generated"
                    className="img-fluid rounded border"
                    style={{ maxHeight: '300px', width: '100%', objectFit: 'contain' }}
                  />
                </div>
                
                {/* Action Buttons */}
                <div className="d-flex gap-2 mt-3">
                  <button
                    type="button"
                    className="btn btn-success flex-grow-1"
                    onClick={handleSaveAIImage}
                    disabled={isSavingAI}
                  >
                    {isSavingAI ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Applying...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check me-2"></i>
                        Apply
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setAiGeneratedImage('');
                      setHasGenerated(false);
                    }}
                  >
                    <i className="fas fa-times me-2"></i>
                    Clear
                  </button>
                </div>
                
                <small className="text-success d-block mt-2">
                  <i className="fas fa-check-circle me-1"></i>
                  Image will be saved to server and added to slider options
                </small>
              </div>
            )}

            <div className="alert alert-info mt-3 mb-0">
              <small>
                <i className="fas fa-info-circle me-1"></i>
                <strong>Tip:</strong> Generated images will be added to the slider. Generation takes 10-30 seconds.
              </small>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIImageGenerator;

